import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { SiteFooter } from "../components/SiteFooter";
import { toast } from "sonner";
import { Crown, ShoppingBag, Flame, Gem, Droplets, Leaf, ChevronDown, ChevronUp, Cookie, ArrowRight, Cigarette, Package } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_da98dbcd-f751-4482-bd4e-a7e20426ba71/artifacts/tm6wbpvj_CD8D187A-3FEA-430F-A897-685C7F8A2178.png";
const HERO_IMG = "https://images.unsplash.com/photo-1580655653885-65763b2597d0?w=1400&q=80";

/* ========== Menu Data ========== */

const KMF_CATEGORIES = [
  { id: "moonrocks", label: "KAWS Rocks", icon: Gem },
  { id: "vapes", label: "Vapes & Carts", icon: Droplets },
  { id: "concentrates", label: "Concentrates", icon: Flame },
  { id: "prerolls", label: "Prerolls", icon: Cigarette },
  { id: "mushrooms", label: "Mushrooms", icon: Cookie },
  { id: "flower", label: "Flower", icon: Leaf },
];

const MOONROCK_EDITIONS = [
  { name: "Slurpee Edition", vibe: "Sweet \u00b7 Fruity \u00b7 Ice Cold", color: "#3B82F6", flavors: ["Blue Slurpee", "Purple Rain", "Watermelon Ice", "Strawberry Slush", "Tropical Punch"] },
  { name: "Cereal Edition", vibe: "Sweet \u00b7 Creamy \u00b7 Nostalgic", color: "#F59E0B", flavors: ["Fruity Pebbles", "Cap'N Crunch", "Cinnamon Toast", "Cocoa Krispies", "Honey Berry"] },
  { name: "Exotic Edition", vibe: "Exotic \u00b7 Loud \u00b7 Potent", color: "#10B981", flavors: ["Zkittlez", "Gelato", "Runtz", "Biscotti", "Sherbert"] },
  { name: "Cake Edition", vibe: "Rich \u00b7 Creamy \u00b7 Dessert", color: "#EC4899", flavors: ["Wedding Cake", "Birthday Cake", "Pink Cake", "Lemon Cake", "Red Velvet"] },
  { name: "Snowcap Edition", vibe: "Icy \u00b7 Minty \u00b7 Smooth", color: "#6EE7B7", flavors: ["Snowcap", "Kush Mints", "Ice Cream Cake", "Frozen Zkittlez", "White Truffle"] },
];

const VAPE_BRANDS_2G = ["Ace Gold","STKY","Jetter Juice","Wizard Trees","Astronaut","Pacman","Muha Meds","Muha Meds All-In-One","Pacman W/ Joint","Tyson","Whole Melt","Hitz","Blinkers","Blinkers Ice Cream","FRYD","FRYD W/ Gummy","FRYD W/ Joint","Big Chief","Raw Garden","Proneke","Snack Drip","Grab & Dab","Choice Labs","Cry Baby","Donuts","Sluggers","Cookies","Cookies X Freak Brothers","Muha Cookies","Sherbinski","Alien Labs","Mad Labs","Magnum","Sprinklez","Buzz Bar","Clean Carts","Cali Honey","Runtz","Gold Coast Clear","Good Extracts","Luigi W/ Joint","Besos","Boutique","Packwoods","Dabwoods","Turn","Plume","DVSNI"];
const CART_BRANDS = ["Jetter Juice", "Muha Meds", "Raw Garden", "Runtz", "TKO", "Breeze 1G Disposable"];

const AUTHENTIC_BRANDS = ["Kik (1G)","Muha Meds","Tsunamiz","Splitz","Luvs","Labubu","Polka Pen","Cali Clear Mega (150 box)","Stky","Trash Bag Boys","Nova","Vapez","Fryd","Favorites","Chrome Cart","Arcadia Fusion","Sugar Rush","Source","Clean","Vior","Hitz","Uni","Fade","Big Chief","Buzz Bar","Whole Melt","Stoner Stix","Muha x Cookies","Buddha Bear (3G)","Cake","Blinkers","Stealthy","Goo'd","Luigi","Astro","Push","Mad Labs","Suckerz Splitz","Boutig Gum","Ace Ultra","Switch","Turn","Dranks","California Gold (3G)","Trios Triple Threat","Sherbinskis Quattro","Raw Garden"];
const REPLICA_BRANDS = ["Fryd","Hitz","Turn","Clean","Luigi","Besos","Plume","Splitz","Runtz","Doja","Packman","Packwoods","Uni","Puffin","Sluggers","Blinkers","Khalifa Kush","ChoicesLab","Muha Meds","Big Chief","Ace Ultra","Whole Melt","Grab & Dab","Smoothie Bar","Big Chief x Fryd","Muha Meds x Cookies","Cookies Lemonade","Cookies Fried Bananas","Sherbinskis Quattro","Backpackboyz","Jungle Boys","Snooze Pillows","Favorites","Wizard Trees","Call Clear","Boutiq Orb","Super Dope"];

const VAPE_PRICING = [
  { name: "2G Disposable", price: "$50" },
  { name: "3G Disposable", price: "$65" },
  { name: "3.5G Disposable", price: "$70" },
  { name: "1G Screw-On Cart", price: "$30" },
  { name: "Wholesale Box (100 pcs)", price: "$1,400", note: "$14/piece" },
];

const CONCENTRATES_SUGARS = [
  { name: "Splitz 1/8 50-50", price: "$50" },{ name: "Snooze Drool 50/50", price: "$50" },{ name: "50/50 Whole M", price: "$50" },{ name: "Cake Badder", price: "$55" },
  { name: "Havana Sugar", price: "$55" },{ name: "Vice City Sugar", price: "$55" },{ name: "Exotic WM Sugar", price: "$55" },{ name: "Winter WM Sugar", price: "$55" },
  { name: "Slurpee WM Sugar", price: "$55" },{ name: "Sprinkles WM Sugar", price: "$55" },{ name: "Gelato WM Sugar", price: "$55" },{ name: "Candy WM Sugar", price: "$55" },
  { name: "Donuts", price: "$55" },{ name: "Whole Melt Extracts", price: "$60" },{ name: "Whole Melt Live Resin", price: "$60" },{ name: "Whole Melt Live Rosin", price: "$70" },
];
const CONCENTRATES_FEATURED = [
  { name: "Dirty Sprite (7G Resin)", price: "$105" },
  { name: "Pure Ice Cream (7G Badder)", price: "$105" },
  { name: "Hash — Hubba Bubba", price: "$55" },
];

const EXTRACTS_UNPACKAGED = ["Badder Jars","Sugar Jars (Grade B)","D8 Liters","Crumble Containers","Sugar Jars (Grade A)","Slabs","CBD Iso Key","THCA Diamond Jars"];
const EXTRACTS_PACKAGED = ["ATC Badder","Whole Melt","Puff LA","Head Stash","Sherbinskis Quattro","Whole Melt Passport Edition","Luminate","Muun Moon Rocks","Growler's Ice Caps","Wavvy","Splitz","Drip Diamonds","Whole Melt Retro Edition","Whole Melt Vending Machine","Splitz The Corner Drop"];

const ROSIN_HASHISH = [
  { name: "LA Mousse (100G Bar)", price: "$600" },
  { name: "Bubble Hash (100G Bar)", price: "$600" },
  { name: "Static Hash (50G Bar)", price: "$600" },
  { name: "Live Rosin 45u (Oz Jar)", price: "DM" },
  { name: "Live Rosin 90u (Oz Jar)", price: "DM" },
  { name: "Whole Melt Rosin (Oz Box)", price: "DM" },
  { name: "Jatt Farms Rosin (Oz Box)", price: "DM" },
];
const HASH_BULK = [
  { name: "1000G", price: "$1,200" },
  { name: "500G", price: "$600" },
];

const PREROLL_BRANDS = ["UA 1G Jeeter","Lucy's 2G","UA Muha Mates","UA BTQ","UA Sluggers","UA Baby Jeeters","Ice Kream Donuts","Devour Blunts","Ripken Woods","Lit Rippz","Frostix","Packman x Cali Gold Donuts","Devour Donuts","Lucy's Hole In One","California Gold Shorties","Astros Minis","Pleasures (Live Rosin)","Pounds Hash Holes","LA Dank Donuts","Hot Ones","Sherbinski Minis Live Rosin"];

const PREPACKAGED_FLOWER = ["Sweetz High Endz","Pillows","Jungle Boys","Yamz Bags","BonBonz","Super Dope","CBX","Yamz Jars","Wizard Trees","LEVELZ","Reptoad"];
const CONCENTRATES_STANDARD = {
  label: "Standard",
  sub: "Resin \u00b7 Badder \u00b7 Crumble \u00b7 Wax",
  items: [
    { size: "1G", price: "$30" },
    { size: "3G", price: "$75" },
    { size: "5G", price: "$100" },
    { size: "7G", price: "$105" },
    { size: "14G (\u00bd OZ)", price: "$210" },
    { size: "1 OZ (28G)", price: "$420" },
  ],
};
const CONCENTRATES_PREMIUM_DIAMOND_SW = {
  label: "Diamond",
  sub: "Sugar Wax",
  items: [
    { size: "1G", price: "$55" },
    { size: "3G", price: "$155" },
    { size: "5G", price: "$245" },
    { size: "7G", price: "$350" },
    { size: "14G (\u00bd OZ)", price: "$700" },
    { size: "1 OZ (28G)", price: "$1,400" },
  ],
};
const CONCENTRATES_PREMIUM_SOLID = {
  label: "Solid Diamond",
  sub: "Sweet Fire Funk — 710Labs",
  items: [
    { size: "1G", price: "$60" },
    { size: "3G", price: "$170" },
    { size: "5G", price: "$270" },
    { size: "7G", price: "$350" },
    { size: "14G (\u00bd OZ)", price: "$700" },
    { size: "28G (1 OZ)", price: "$1,400" },
  ],
};
const CONCENTRATES_PREMIUM_ROSIN = {
  label: "Live Rosin",
  sub: "Dank Rosin — Solventless",
  items: [
    { size: "1G", price: "$60" },
    { size: "3G", price: "$170" },
    { size: "5G", price: "$270" },
    { size: "7G", price: "$350" },
    { size: "14G (\u00bd OZ)", price: "$700" },
    { size: "28G (1 OZ)", price: "$1,400" },
  ],
};

const MUSHROOM_EDIBLE_RETAIL = [
  { name: "Waffle Cones (4G)", price: "$50" },
  { name: "Mushroom Chocolate Bar", price: "$50" },
  { name: "Mushroom Gummies", price: "$40" },
  { name: "Vision Mushroom Gummies", price: "$60" },
];

const MUSHROOM_BRANDS = [
  { name: "PolkaDot Chocolate Bars", flavors: ["Lucky Charms","Fruity Pebbles","Cinnamon Toast Crunch","Cookie Crisp","Rice Krispies","Frosted Flakes","Cereal Milk","Cookies & Cream","Blueberry Muffin","Raspberry Muffin","Strawnana","Reese's","Butter Pecan","Macadamia Nut Cookies","PB&J","Godiva Strawberries","Toblerone","Milk Chocolate","Viet Ice Coffee","Jerry Garcia"] },
  { name: "One Up Chocolate Bars", flavors: ["Assorted Premium Flavors"] },
  { name: "Boomer's Chocolate Bars", flavors: ["Assorted Flavors"] },
  { name: "Magic Ruth Chocolate Bars", flavors: ["Assorted Flavors"] },
  { name: "Tripz Chocolate Bars", flavors: ["Milk Chocolate","Cookie","Caramel"] },
  { name: "Smackers Chocolate Bars", flavors: ["Chocolate","Peanut","Caramel","Nougat"] },
  { name: "Silly's Peanut Butter Cups", flavors: ["Peanut Butter Cups"] },
  { name: "Waffle Cones", flavors: ["Oreo Milkshake","Blueberry Yogurt","Salted Caramel","Strawberry n Cream"] },
  { name: "Mushroom Mini Cones", flavors: ["Assorted Mini Cones","Variety Flavors"] },
  { name: "Fuzion Gummies", flavors: ["Assorted Flavors"] },
  { name: "Golden Ticket Gummies", flavors: ["Assorted Flavors"] },
  { name: "Golden Ticket Bars", flavors: ["Assorted Flavors"] },
  { name: "Real Mush Bars", flavors: ["Assorted Flavors"] },
  { name: "Polka Dot Gummies", flavors: ["Assorted Flavors"] },
  { name: "Polka Dot Tabs", flavors: ["Assorted Varieties"] },
  { name: "One Up Coned", flavors: ["Assorted Flavors"] },
  { name: "Trippy Flip / Bomb Bars", flavors: ["Assorted Flavors"] },
];

const MUSHROOM_RAW_PRICING = [
  { name: "Oz", price: "$130" },
  { name: "Half P", price: "$600" },
  { name: "Full Unit", price: "$1,000" },
];

const MUSHROOM_STRAINS = [
  { name: "Penis Envy", potency: 5, desc: "The king of potency. Heavy, intense, deep mental euphoria and spiritual journeys." },
  { name: "PE x Blue Meanie", potency: 5, desc: "Next level experience. Visuals, euphoria, deep and mind-bending creative flow." },
  { name: "Iceberg", potency: 4, desc: "Crystal clear journeys. Clean, uplifting, pure mental clarity and spiritual connect." },
  { name: "Makilla Gorilla", potency: 4, desc: "Body melt power. Relaxing, heavy, grounding and calm. Mood enhancer." },
  { name: "A.P.E.", potency: 5, desc: "Advanced psychedelics. Potent, long lasting, intense visuals, epic experiences." },
  { name: "Blue Vuitton", potency: 4, desc: "Smooth, luxurious experience. Clean visuals, euphoric headspace, elevated vibes." },
  { name: "Golden Teacher", potency: 3, desc: "The classic. Warm, insightful journeys. Perfect for first-timers and deep thinkers." },
];

const FLOWER_ITEMS = [
  { name: "Lows", price: "$700+" },
  { name: "Mid", price: "$900+" },
  { name: "Top", price: "$1,200+" },
  { name: "ZA", price: "$1,500+" },
  { name: "Boutique (Small Batch)", price: "$2,200+" },
];
const BREAKDOWN_PACKS = [
  { name: "Oz (strain dependent)", price: "$100 \u2013 $250", note: "First come first serve til that strain is gone" },
];
const OZ_SPECIALS = [
  { name: "Zour Smallz", price: "$100", tag: "Small buds. Big flavor. Same fire, same quality." },
  { name: "Frog Venom", price: "$100", tag: "The toxic strain that hits different. Dense, frosty, heavy hitter." },
];
const EDIBLE_ITEMS = [
  { name: "Candy", price: "$30" },
  { name: "Chocolate", price: "$30 \u2013 $50" },
  { name: "Gummies", price: "$40" },
];
const THC_EDIBLE_BRANDS = ["Almond Joint", "Reefers", "Mr. Goodbud", "THC Snickers", "THC Twix", "Crunch THC", "Hershey THC", "Milk Weed", "Payweed", "Almond Weed", "Marijuanos", "Dank Nuggs (Jar)"];
const THC_CANDY_BRANDS = [
  "Trolli Cherry Bombers", "Trolli Big Bold Bears", "Trolli Apple O's", "Trolli Squiggles",
  "HYFLY Blue Raspberry Bears", "HYFLY Tangerine Bears", "HYFLY Sour Watermelon Rings",
  "HYFLY Sour Bears Neon", "HYFLY Sour Blue Raspberry Rings", "HYFLY Worms Mix",
  "Medicated Nerds Rope Bites", "Med Head Blueberry", "Medibles Doritos-Style", "Medibles Cheetos-Style",
  "Candy Fruits Strawnana", "Candy Fruits Variety", "Dank Gummies", "Weedish Fish",
  "Baribo Happy Cherries", "Chuckles Gummy Worms", "Neon Worms", "Warheads Sour Chewy Cubes",
  "Sour Patch Medicated", "Sour Strawberry Watermelon Bites",
  "Cap'n Crunch Berries Edible", "One Up Gummies", "LOL Edibles Candy",
  "Mango Kush Gummies", "Cannabis Crunchy PB Candy",
  /* New brands */
  "Bored Hash Gummies", "Nugz Bars", "Clarity Gummies", "Cereal Bars",
  "Squish Mini", "MedLab Bars", "Galaxy Sour Belts", "California Gold Gummies",
  "Devour Gummies/Ropes", "Fade Gummies", "Golden Ticket Gummies",
  "Pounds Gummies", "Squish Gummies", "Pounds Nuggz",
  "Devour Syrups", "Frootz Gummies", "Pounds Syrup",
];
const MOONROCK_ITEMS = [{ name: "Oz", price: "$250" },{ name: "Full Box (16 Jars)", price: "$1,400" }];

/* ========== Flyer / Product Showcase Images (Logo-free redesigns) ========== */
const SHOWCASE = {
  moonrocks: [
    { src: "https://static.prod-images.emergentagent.com/jobs/da98dbcd-f751-4482-bd4e-a7e20426ba71/images/040d5f2d16eb10b0d04362f475eb40d6e988d8e722dda693830d37c0fe7496c5.jpeg", alt: "KAWS Rocks Moonrock Collection", pos: "center 55%" },
  ],
  vapes: [
    { src: "https://static.prod-images.emergentagent.com/jobs/da98dbcd-f751-4482-bd4e-a7e20426ba71/images/dd7d6a38d49e1739d00c4d30e09520f8d311cae3d0ceddfd06c6251ad3a76ad1.jpeg", alt: "Premium Disposable Vapes", pos: "center 50%" },
  ],
  concentrates: [
    { src: "https://static.prod-images.emergentagent.com/jobs/da98dbcd-f751-4482-bd4e-a7e20426ba71/images/cb5fe0dd98a57048b44285fc722dfbba26a38bda9735114aff2c529a42d17641.jpeg", alt: "Concentrates Collection", pos: "center 50%" },
    { src: "https://static.prod-images.emergentagent.com/jobs/da98dbcd-f751-4482-bd4e-a7e20426ba71/images/c13f187575db7d6c2486b54cd7ca3b1dacc286ebe373977cbda97175f13ca12f.jpeg", alt: "Sugars & Badders Menu", pos: "center 50%" },
  ],
  mushrooms: [
    { src: "https://static.prod-images.emergentagent.com/jobs/da98dbcd-f751-4482-bd4e-a7e20426ba71/images/bbe68b3d86c2e9da5b1aaf74c7f6d2c6acc2568aeed9eec0f4814dde9b093652.jpeg", alt: "Mushroom Edibles Collection", pos: "center 40%" },
    { src: "https://static.prod-images.emergentagent.com/jobs/da98dbcd-f751-4482-bd4e-a7e20426ba71/images/021208d5e6fe0ed7815ec7622a16e9efa3c25d9b609a7f5fabe559cc3b734199.jpeg", alt: "Premium Mushroom Strains", pos: "center 45%" },
  ],
};

/* ========== Helpers ========== */

function PriceRow({ name, price, isSoldOut }) {
  return (
    <div className="flex justify-between items-center py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", opacity: isSoldOut ? 0.35 : 1 }}>
      <div className="flex items-center gap-2">
        <span className="text-sm" style={{ color: "rgba(240,240,240,0.7)", textDecoration: isSoldOut ? "line-through" : "none" }}>{name}</span>
        {isSoldOut && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5" style={{ background: "rgba(239,68,68,0.15)", color: "#EF4444" }}>Sold Out</span>}
      </div>
      <span className="text-sm font-semibold" style={{ color: "#a855f7", fontFamily: "'JetBrains Mono', monospace" }}>{price}</span>
    </div>
  );
}

function PotencyDots({ count }) {
  return (
    <div className="flex gap-1">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="w-2 h-2 rounded-full" style={{ background: i < count ? "#a855f7" : "rgba(255,255,255,0.08)" }} />
      ))}
    </div>
  );
}

function ShowcaseGallery({ images, label }) {
  const [lightbox, setLightbox] = useState(null);
  const cols = images.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1";
  return (
    <div className="mb-10" data-testid={`showcase-${label?.replace(/\s/g,'-').toLowerCase()}`}>
      <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-3" style={{ color: "rgba(168,85,247,0.45)" }}>Product Showcase</p>
      <div className={`grid ${cols} gap-3`}>
        {images.map((img, idx) => (
          <button key={idx} onClick={() => setLightbox(img)} className="relative overflow-hidden group text-left cursor-pointer"
            style={{ borderRadius: "12px", border: "1px solid rgba(168,85,247,0.12)", background: "#0d0d1f", height: images.length > 1 ? "240px" : "320px" }}>
            <img src={img.src} alt={img.alt} loading="lazy" className="absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-[1.03]"
              style={{ objectFit: "cover", objectPosition: img.pos || "center" }} />
            {/* soft top fade */}
            <div className="absolute inset-x-0 top-0" style={{ height: "25%", background: "linear-gradient(180deg, rgba(10,10,24,0.5) 0%, transparent 100%)" }} />
            {/* bottom blend */}
            <div className="absolute inset-x-0 bottom-0" style={{ height: "30%", background: "linear-gradient(0deg, rgba(10,10,24,0.7) 0%, transparent 100%)" }} />
            {/* subtle vignette */}
            <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 50%, rgba(10,10,24,0.4) 100%)" }} />
            {/* hover overlay */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
              style={{ background: "rgba(10,10,24,0.3)" }}>
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase px-4 py-2"
                style={{ background: "rgba(168,85,247,0.15)", border: "1px solid rgba(168,85,247,0.25)", color: "#c084fc", backdropFilter: "blur(6px)", borderRadius: "4px" }}>View Full Menu</span>
            </div>
            {/* bottom label */}
            <div className="absolute bottom-3 left-3 right-3">
              <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "rgba(168,85,247,0.6)" }}>{img.alt}</p>
            </div>
          </button>
        ))}
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8" style={{ background: "rgba(0,0,0,0.93)" }}
          onClick={() => setLightbox(null)} data-testid="showcase-lightbox">
          <div className="relative max-w-5xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.alt} className="w-full h-auto max-h-[85vh] object-contain" style={{ borderRadius: "8px" }} />
            <button onClick={() => setLightbox(null)} className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center text-sm font-bold"
              style={{ background: "#a855f7", color: "#fff", borderRadius: "50%", lineHeight: 1 }} data-testid="lightbox-close">&times;</button>
            <p className="text-center mt-3 text-xs" style={{ color: "rgba(240,240,240,0.35)" }}>{lightbox.alt}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== Component ========== */

export default function RetailMenuPage() {
  const { submissionId } = useParams();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(!!submissionId);
  const [activeTab, setActiveTab] = useState("kookamf");
  const [activeCategory, setActiveCategory] = useState("moonrocks");
  const [expandedBrand, setExpandedBrand] = useState(null);
  const [vapeSearch, setVapeSearch] = useState("");
  const [soldOut, setSoldOut] = useState(new Set());

  const fetchSubmission = useCallback(async () => {
    if (!submissionId) return;
    try { const res = await axios.get(`${API}/submissions/${submissionId}`); setSubmission(res.data); }
    catch { /* still show the menu even if submission lookup fails */ }
    finally { setLoading(false); }
  }, [submissionId]);

  useEffect(() => { fetchSubmission(); }, [fetchSubmission]);

  useEffect(() => {
    axios.get(`${API}/sold-out`).then((res) => setSoldOut(new Set(res.data))).catch(() => {});
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0a18" }}>
      <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "#a855f7", borderTopColor: "transparent" }} />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-primary)" }} data-testid="retail-menu-page">
      {/* Hero */}
      <div className="relative" style={{ height: "clamp(200px, 32vh, 300px)" }}>
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,50,33,0.6) 0%, rgba(15,50,33,0.9) 100%)" }} />
        <div className="relative z-10 h-full flex flex-col justify-between px-5 sm:px-8 md:px-12 lg:px-20 py-6 md:py-8">
          <a href="/"><img src={LOGO_URL} alt="Green Routes" className="h-9 sm:h-10 md:h-12 self-start logo-on-dark" /></a>
          <div>
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-2 font-semibold" style={{ color: "rgba(253,252,247,0.4)" }}>Retail &middot; East Coast</p>
            <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight" style={{ color: "#F5F3E9", fontFamily: "'Playfair Display', serif" }}>Shop the Menu</h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-5 sm:px-8 md:px-12 lg:px-20 py-5" style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="max-w-3xl mx-auto flex gap-2">
          <button onClick={() => setActiveTab("kookamf")} data-testid="tab-kookamf" className="flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold transition-all"
            style={{ background: activeTab === "kookamf" ? "#1a1a2e" : "transparent", color: activeTab === "kookamf" ? "#a855f7" : "var(--text-tertiary)", border: activeTab === "kookamf" ? "1px solid #a855f7" : "1px solid var(--border)" }}>
            <Crown size={14} /> KookaMF
          </button>
          <button onClick={() => setActiveTab("sourced")} data-testid="tab-sourced" className="flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold transition-all"
            style={{ background: activeTab === "sourced" ? "var(--primary)" : "transparent", color: activeTab === "sourced" ? "#F5F3E9" : "var(--text-tertiary)", border: activeTab === "sourced" ? "1px solid var(--primary)" : "1px solid var(--border)" }}>
            <ShoppingBag size={14} /> Green Routes Sourced
          </button>
        </div>
      </div>

      <div className="flex-1">
        {activeTab === "kookamf" && (
          <div data-testid="kookamf-menu" style={{ background: "#0a0a18" }}>
            {/* Brand Header */}
            <div className="px-5 sm:px-8 md:px-12 lg:px-20 pt-12 pb-6 text-center" data-testid="kookamf-brand-header">
              <p className="text-xs tracking-[0.3em] uppercase mb-2 font-bold" style={{ color: "#a855f7" }}>Exclusive Partner</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl tracking-tight mb-2" style={{ color: "#f0f0f0", fontFamily: "'Playfair Display', serif" }}>KookaMungaFunga</h2>
              <p className="text-xs max-w-sm mx-auto" style={{ color: "rgba(240,240,240,0.35)" }}>Premium quality. Lab tested. Locked-in home base — exclusively tied to Green Routes.</p>
            </div>

            {/* Category Nav */}
            <div className="px-5 sm:px-8 md:px-12 lg:px-20 pb-1" style={{ borderBottom: "1px solid rgba(168,85,247,0.1)" }}>
              <div className="max-w-3xl mx-auto flex gap-1 overflow-x-auto pb-1">
                {KMF_CATEGORIES.map((cat) => (
                  <button key={cat.id} onClick={() => setActiveCategory(cat.id)} data-testid={`cat-${cat.id}`}
                    className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-semibold whitespace-nowrap transition-all"
                    style={{ background: activeCategory === cat.id ? "rgba(168,85,247,0.12)" : "transparent", color: activeCategory === cat.id ? "#a855f7" : "rgba(240,240,240,0.3)", borderBottom: activeCategory === cat.id ? "2px solid #a855f7" : "2px solid transparent" }}>
                    <cat.icon size={13} /> {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="px-5 sm:px-8 md:px-12 lg:px-20 py-10">
              <div className="max-w-3xl mx-auto">

                {/* ===== MOONROCKS ===== */}
                {activeCategory === "moonrocks" && (
                  <div>
                    <ShowcaseGallery images={SHOWCASE.moonrocks} label="moonrocks" />
                    <h3 className="text-lg font-medium mb-1" style={{ color: "#f0f0f0", fontFamily: "'Playfair Display', serif" }}>KAWS Rocks — Moonrock Menu</h3>
                    <p className="text-xs mb-6" style={{ color: "rgba(240,240,240,0.35)" }}>Hand crafted &middot; Kief coated &middot; Liquid diamonds &middot; Max potency</p>
                    <div className="mb-8" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(168,85,247,0.08)", padding: "1px" }}>
                      {MOONROCK_ITEMS.map((item) => <PriceRow key={item.name} name={item.name} price={item.price} isSoldOut={soldOut.has(item.name)} />)}
                    </div>
                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Editions</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {MOONROCK_EDITIONS.map((ed) => (
                        <div key={ed.name} className="p-4" style={{ borderLeft: `3px solid ${ed.color}`, background: "rgba(255,255,255,0.02)" }}>
                          <p className="text-sm font-semibold" style={{ color: "#f0f0f0" }}>{ed.name}</p>
                          <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: ed.color }}>{ed.vibe}</p>
                          {ed.flavors.map((f) => <p key={f} className="text-xs" style={{ color: "rgba(240,240,240,0.5)" }}>{f}</p>)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ===== VAPES ===== */}
                {activeCategory === "vapes" && (
                  <div>
                    <ShowcaseGallery images={SHOWCASE.vapes} label="vapes" />
                    <h3 className="text-lg font-medium mb-1" style={{ color: "#f0f0f0", fontFamily: "'Playfair Display', serif" }}>Vapes & Carts</h3>
                    <p className="text-xs mb-6" style={{ color: "rgba(240,240,240,0.35)" }}>Top brands &middot; Big flavors &middot; Premium disposables</p>

                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Retail Pricing</p>
                    <div className="mb-8" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(168,85,247,0.08)", padding: "1px" }}>
                      {VAPE_PRICING.map((item) => (
                        <div key={item.name} className="flex justify-between items-center py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                          <div>
                            <span className="text-sm" style={{ color: "rgba(240,240,240,0.7)" }}>{item.name}</span>
                            {item.note && <span className="text-xs ml-2" style={{ color: "rgba(168,85,247,0.5)" }}>({item.note})</span>}
                          </div>
                          <span className="text-sm font-semibold" style={{ color: "#a855f7", fontFamily: "'JetBrains Mono', monospace" }}>{item.price}</span>
                        </div>
                      ))}
                    </div>

                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Cart Brands — 1G Screw-On</p>
                    <div className="flex flex-wrap gap-1.5 mb-8">
                      {CART_BRANDS.map((b) => (
                        <span key={b} className="px-3 py-1.5 text-xs" style={{ background: "rgba(168,85,247,0.05)", border: "1px solid rgba(168,85,247,0.1)", color: "rgba(240,240,240,0.6)" }}>{b}</span>
                      ))}
                    </div>

                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>All Disposable Brands</p>
                    <input type="text" value={vapeSearch} onChange={(e) => setVapeSearch(e.target.value)} placeholder="Search brands..." data-testid="vape-brand-search"
                      className="w-full text-xs py-2 px-3 outline-none mb-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(168,85,247,0.1)", color: "rgba(240,240,240,0.7)" }} />
                    <div className="flex flex-wrap gap-1.5">
                      {VAPE_BRANDS_2G.filter((b) => b.toLowerCase().includes(vapeSearch.toLowerCase())).map((brand) => (
                        <span key={brand} className="px-3 py-1.5 text-xs" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(168,85,247,0.06)", color: "rgba(240,240,240,0.5)" }}>{brand}</span>
                      ))}
                      {vapeSearch && VAPE_BRANDS_2G.filter((b) => b.toLowerCase().includes(vapeSearch.toLowerCase())).length === 0 && (
                        <p className="text-xs py-2" style={{ color: "rgba(240,240,240,0.3)" }}>No brands match &ldquo;{vapeSearch}&rdquo;</p>
                      )}
                    </div>

                    <p className="text-xs uppercase tracking-wider font-bold mt-8 mb-3" style={{ color: "#a855f7" }}>Authentic Disposables</p>
                    <div className="flex flex-wrap gap-1.5 mb-6">
                      {AUTHENTIC_BRANDS.map((b) => (
                        <span key={b} className="px-3 py-1.5 text-xs" style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.12)", color: "rgba(240,240,240,0.6)" }}>{b}</span>
                      ))}
                    </div>

                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Replica Disposables</p>
                    <div className="flex flex-wrap gap-1.5">
                      {REPLICA_BRANDS.map((b) => (
                        <span key={b} className="px-3 py-1.5 text-xs" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(168,85,247,0.06)", color: "rgba(240,240,240,0.45)" }}>{b}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* ===== CONCENTRATES ===== */}
                {activeCategory === "concentrates" && (
                  <div>
                    <ShowcaseGallery images={SHOWCASE.concentrates} label="concentrates" />
                    <h3 className="text-lg font-medium mb-1" style={{ color: "#f0f0f0", fontFamily: "'Playfair Display', serif" }}>Premium Extracts</h3>
                    <p className="text-xs mb-6" style={{ color: "rgba(240,240,240,0.35)" }}>Max potency &middot; Clean extraction &middot; Lab tested &middot; Small batch</p>
                    {/* Standard */}
                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "rgba(240,240,240,0.35)" }}>Standard</p>
                    <div className="mb-8 p-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(168,85,247,0.06)" }}>
                      <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "rgba(168,85,247,0.5)" }}>{CONCENTRATES_STANDARD.sub}</p>
                      {CONCENTRATES_STANDARD.items.map((item) => (
                        <div key={item.size} className="flex justify-between py-1.5">
                          <span className="text-sm" style={{ color: "rgba(240,240,240,0.5)" }}>{item.size}</span>
                          <span className="text-sm font-semibold" style={{ color: "#a855f7", fontFamily: "'JetBrains Mono', monospace" }}>{item.price}</span>
                        </div>
                      ))}
                    </div>

                    {/* Premium */}
                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Premium</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
                      {[CONCENTRATES_PREMIUM_DIAMOND_SW, CONCENTRATES_PREMIUM_SOLID, CONCENTRATES_PREMIUM_ROSIN].map((tier) => (
                        <div key={tier.label} className="p-4" style={{ background: "rgba(168,85,247,0.04)", border: "1px solid rgba(168,85,247,0.1)" }}>
                          <p className="text-sm font-semibold" style={{ color: "#f0f0f0" }}>{tier.label}</p>
                          <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "rgba(168,85,247,0.5)" }}>{tier.sub}</p>
                          {tier.items.map((item) => (
                            <div key={item.size} className="flex justify-between py-1.5">
                              <span className="text-sm" style={{ color: "rgba(240,240,240,0.5)" }}>{item.size}</span>
                              <span className="text-sm font-semibold" style={{ color: "#a855f7", fontFamily: "'JetBrains Mono', monospace" }}>{item.price}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Sugars & Badders — 1G</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6" style={{ background: "rgba(255,255,255,0.01)", border: "1px solid rgba(168,85,247,0.04)", padding: "0 16px" }}>
                      {CONCENTRATES_SUGARS.map((item) => <PriceRow key={item.name} name={item.name} price={item.price} isSoldOut={soldOut.has(item.name)} />)}
                    </div>
                    <p className="text-xs uppercase tracking-wider font-bold mt-8 mb-3" style={{ color: "#a855f7" }}>Featured — In Stock</p>
                    <div className="mb-2" style={{ background: "rgba(168,85,247,0.04)", border: "1px solid rgba(168,85,247,0.1)", padding: "1px 16px" }}>
                      {CONCENTRATES_FEATURED.map((item) => <PriceRow key={item.name} name={item.name} price={item.price} isSoldOut={soldOut.has(item.name)} />)}
                    </div>

                    <p className="text-xs uppercase tracking-wider font-bold mt-8 mb-3" style={{ color: "#a855f7" }}>Extracts — Unpackaged</p>
                    <div className="flex flex-wrap gap-1.5 mb-6">
                      {EXTRACTS_UNPACKAGED.map((b) => (
                        <span key={b} className="px-3 py-1.5 text-xs" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(168,85,247,0.06)", color: "rgba(240,240,240,0.5)" }}>{b}</span>
                      ))}
                    </div>
                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Extracts — Packaged Brands</p>
                    <div className="flex flex-wrap gap-1.5 mb-6">
                      {EXTRACTS_PACKAGED.map((b) => (
                        <span key={b} className="px-3 py-1.5 text-xs" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(168,85,247,0.06)", color: "rgba(240,240,240,0.5)" }}>{b}</span>
                      ))}
                    </div>

                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Rosin / Hashish</p>
                    <div className="mb-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(168,85,247,0.08)", padding: "1px 16px" }}>
                      {ROSIN_HASHISH.map((item) => <PriceRow key={item.name} name={item.name} price={item.price} isSoldOut={soldOut.has(item.name)} />)}
                    </div>
                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Hash — Bulk Pricing</p>
                    <div style={{ background: "rgba(168,85,247,0.04)", border: "1px solid rgba(168,85,247,0.1)", padding: "1px 16px" }}>
                      {HASH_BULK.map((item) => <PriceRow key={item.name} name={item.name} price={item.price} isSoldOut={soldOut.has(item.name)} />)}
                    </div>
                  </div>
                )}

                {/* ===== PREROLLS ===== */}
                {activeCategory === "prerolls" && (
                  <div>
                    <h3 className="text-lg font-medium mb-1" style={{ color: "#f0f0f0", fontFamily: "'Playfair Display', serif" }}>Prerolls</h3>
                    <p className="text-xs mb-6" style={{ color: "rgba(240,240,240,0.35)" }}>Jeeters &middot; Blunts &middot; Donuts &middot; Hash Holes &middot; Live Rosin</p>
                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Available Brands</p>
                    <div className="flex flex-wrap gap-1.5">
                      {PREROLL_BRANDS.map((b) => (
                        <span key={b} className="px-3 py-1.5 text-xs" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(168,85,247,0.06)", color: "rgba(240,240,240,0.6)" }}>{b}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* ===== MUSHROOMS ===== */}
                {activeCategory === "mushrooms" && (
                  <div>
                    <ShowcaseGallery images={SHOWCASE.mushrooms} label="mushrooms" />
                    <h3 className="text-lg font-medium mb-1" style={{ color: "#f0f0f0", fontFamily: "'Playfair Display', serif" }}>Mushroom Menu</h3>
                    <p className="text-xs mb-6" style={{ color: "rgba(240,240,240,0.35)" }}>100% natural &middot; Lab tested &middot; Micro dose or go deep</p>

                    {/* Retail Pricing */}
                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Retail Pricing</p>
                    <div className="mb-8" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(168,85,247,0.08)", padding: "1px" }}>
                      {MUSHROOM_EDIBLE_RETAIL.map((item) => <PriceRow key={item.name} name={item.name} price={item.price} isSoldOut={soldOut.has(item.name)} />)}
                    </div>

                    {/* Raw Bulk */}
                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Raw Mushrooms — Bulk</p>
                    <div className="mb-8" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(168,85,247,0.08)", padding: "1px" }}>
                      {MUSHROOM_RAW_PRICING.map((item) => <PriceRow key={item.name} name={item.name} price={item.price} isSoldOut={soldOut.has(item.name)} />)}
                    </div>

                    {/* Strains */}
                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Premium Strains</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
                      {MUSHROOM_STRAINS.map((s) => (
                        <div key={s.name} className="p-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(168,85,247,0.06)" }}>
                          <p className="text-sm font-semibold mb-1" style={{ color: "#f0f0f0" }}>{s.name}</p>
                          <PotencyDots count={s.potency} />
                          <p className="text-xs mt-2" style={{ color: "rgba(240,240,240,0.4)", lineHeight: 1.5 }}>{s.desc}</p>
                        </div>
                      ))}
                    </div>

                    {/* Edible Brands */}
                    <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Edible Brands &amp; Flavors</p>
                    <div className="space-y-1.5">
                      {MUSHROOM_BRANDS.map((brand) => (
                        <div key={brand.name} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(168,85,247,0.04)" }}>
                          <button onClick={() => setExpandedBrand(expandedBrand === brand.name ? null : brand.name)}
                            className="w-full flex items-center justify-between p-3.5 text-left" data-testid={`brand-${brand.name.replace(/\s/g, '-').toLowerCase()}`}>
                            <span className="text-sm font-medium" style={{ color: "rgba(240,240,240,0.8)" }}>{brand.name}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs" style={{ color: "rgba(240,240,240,0.2)" }}>{brand.flavors.length}</span>
                              {expandedBrand === brand.name ? <ChevronUp size={14} style={{ color: "rgba(240,240,240,0.2)" }} /> : <ChevronDown size={14} style={{ color: "rgba(240,240,240,0.2)" }} />}
                            </div>
                          </button>
                          {expandedBrand === brand.name && (
                            <div className="px-3.5 pb-3.5 flex flex-wrap gap-1.5">
                              {brand.flavors.map((f) => (
                                <span key={f} className="text-xs px-2.5 py-1" style={{ background: "rgba(168,85,247,0.06)", color: "rgba(240,240,240,0.5)", border: "1px solid rgba(168,85,247,0.08)" }}>{f}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ===== FLOWER ===== */}
                {activeCategory === "flower" && (
                  <div>
                    <h3 className="text-lg font-medium mb-1" style={{ color: "#f0f0f0", fontFamily: "'Playfair Display', serif" }}>Flower, Edibles & Packs</h3>
                    <p className="text-xs mb-6" style={{ color: "rgba(240,240,240,0.35)" }}>Priced per pound unless noted</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                      <div className="p-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(168,85,247,0.06)" }}>
                        <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Flower (per lb)</p>
                        {FLOWER_ITEMS.map((item) => <PriceRow key={item.name} name={item.name} price={item.price} isSoldOut={soldOut.has(item.name)} />)}
                      </div>
                      <div className="p-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(168,85,247,0.06)" }}>
                        <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Edibles</p>
                        {EDIBLE_ITEMS.map((item) => <PriceRow key={item.name} name={item.name} price={item.price} isSoldOut={soldOut.has(item.name)} />)}
                        <p className="text-[10px] uppercase tracking-wider font-bold mt-4 mb-2" style={{ color: "rgba(168,85,247,0.5)" }}>THC Chocolate Brands</p>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {THC_EDIBLE_BRANDS.map((b) => (
                            <span key={b} className="text-xs px-2 py-1" style={{ background: "rgba(168,85,247,0.06)", color: "rgba(240,240,240,0.55)", border: "1px solid rgba(168,85,247,0.08)" }}>{b}</span>
                          ))}
                        </div>
                        <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: "rgba(168,85,247,0.5)" }}>Gummies &amp; Candy</p>
                        <div className="flex flex-wrap gap-1">
                          {THC_CANDY_BRANDS.map((b) => (
                            <span key={b} className="text-xs px-2 py-1" style={{ background: "rgba(168,85,247,0.06)", color: "rgba(240,240,240,0.55)", border: "1px solid rgba(168,85,247,0.08)" }}>{b}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="p-4 mb-4" style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.08) 0%, rgba(245,158,11,0.06) 100%)", border: "1px solid rgba(168,85,247,0.18)" }}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[10px] uppercase tracking-[0.2em] font-black px-2 py-0.5" style={{ background: "#F59E0B", color: "#0a0a18" }}>Limited</span>
                        <p className="text-xs uppercase tracking-wider font-bold" style={{ color: "#F59E0B" }}>Oz Specials &mdash; Until It&apos;s Gone</p>
                      </div>
                      {OZ_SPECIALS.map((item) => (
                        <div key={item.name} className="mb-3 last:mb-0">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold" style={{ color: "#f0f0f0" }}>{item.name}</span>
                            <span className="text-sm font-bold" style={{ color: "#F59E0B", fontFamily: "'JetBrains Mono', monospace" }}>{item.price}/oz</span>
                          </div>
                          <p className="text-[10px] mt-0.5" style={{ color: "rgba(240,240,240,0.4)" }}>{item.tag}</p>
                        </div>
                      ))}
                    </div>
                    <div className="p-4" style={{ background: "rgba(168,85,247,0.04)", border: "1px solid rgba(168,85,247,0.1)" }}>
                      <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: "#a855f7" }}>Breakdown Packs</p>
                      {BREAKDOWN_PACKS.map((item) => (
                        <div key={item.name}>
                          <PriceRow name={item.name} price={item.price} isSoldOut={soldOut.has(item.name)} />
                          {item.note && <p className="text-xs pb-2" style={{ color: "rgba(168,85,247,0.4)", fontStyle: "italic" }}>{item.note}</p>}
                        </div>
                      ))}
                    </div>

                    <p className="text-xs uppercase tracking-wider font-bold mt-6 mb-3" style={{ color: "#a855f7" }}>Pre-Packaged Flower Brands</p>
                    <div className="flex flex-wrap gap-1.5">
                      {PREPACKAGED_FLOWER.map((b) => (
                        <span key={b} className="px-3 py-1.5 text-xs" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(168,85,247,0.06)", color: "rgba(240,240,240,0.6)" }}>{b}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 sm:px-8 md:px-12 lg:px-20 py-4 text-center" style={{ borderTop: "1px solid rgba(168,85,247,0.06)" }}>
              <p className="text-[10px]" style={{ color: "rgba(240,240,240,0.15)" }}>21+ Adults Only &middot; Keep Out of Reach of Children &middot; Know Your Laws</p>
            </div>
          </div>
        )}

        {activeTab === "sourced" && (
          <div className="px-5 sm:px-8 md:px-12 lg:px-20 py-12" data-testid="sourced-menu">
            <div className="max-w-2xl mx-auto p-8 text-center" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }} data-testid="sourced-coming-soon">
              <ShoppingBag size={28} className="mx-auto mb-4" style={{ color: "var(--text-tertiary)" }} />
              <h3 className="text-lg mb-2" style={{ color: "var(--text-primary)", fontFamily: "'Playfair Display', serif" }}>Green Routes Sourced</h3>
              <p className="text-sm" style={{ color: "var(--text-secondary)", lineHeight: "1.6", maxWidth: "400px", margin: "0 auto" }}>Beyond KookaMF, Green Routes sources from a curated network across California. Updated seasonally.</p>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="px-5 sm:px-8 md:px-12 lg:px-20 py-10 text-center" style={{ background: "var(--bg-primary)", borderTop: "1px solid var(--border)" }}>
          <ShoppingBag size={20} className="mx-auto mb-3" style={{ color: "#C2693D" }} />
          <p className="text-base font-medium mb-4" style={{ color: "var(--text-primary)" }}>Ready to order?</p>
          <a href="/order" data-testid="btn-place-order"
            className="inline-flex items-center justify-center gap-2 px-10 py-3.5 text-sm font-semibold tracking-wide mb-6"
            style={{ background: "var(--primary)", color: "#F5F3E9" }}>
            Place Order <ArrowRight size={16} />
          </a>
          <div className="space-y-2">
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Or reach us directly:</p>
            <a href="https://signal.me/#eu/CuZMLQ87d4S--6Tw3mN-Td6Ha3ReJZoRoDfeo0imcMmOicmhdPJNhZvSlWFVVHkb" target="_blank" rel="noopener noreferrer"
              className="block text-sm font-semibold underline underline-offset-4" style={{ color: "var(--primary)" }} data-testid="signal-link">Message us on Signal</a>
            <a href="mailto:Greenroutesconsulting@gmail.com" className="block text-sm underline underline-offset-2" style={{ color: "var(--primary)" }} data-testid="retail-email-link">Greenroutesconsulting@gmail.com</a>
          </div>
        </div>
      </div>

      <SiteFooter submissionId={submission?.id} />

      {/* Mobile sticky Order Now button */}
      <a href="/order" data-testid="mobile-order-btn"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-5 py-3 text-sm font-bold tracking-wide lg:hidden"
        style={{ background: "#a855f7", color: "#fff", borderRadius: "40px", boxShadow: "0 4px 20px rgba(168,85,247,0.4)" }}>
        <ShoppingBag size={16} /> Order Now
      </a>
    </div>
  );
}
